import { useState, useEffect, useCallback } from 'react';
import type { VoiceType } from '../types/api';

interface VoiceFilterOption {
  label: string;
  value: string;
}

function buildVoiceFilters(list: VoiceType[]): VoiceFilterOption[] {
  const options: VoiceFilterOption[] = [{ label: '全部音色', value: '' }];

  const genders = Array.from(new Set(list.map((v) => v.gender).filter(Boolean) as string[]));
  genders.forEach((gender) => {
    const label = gender === 'male' ? '男声' : gender === 'female' ? '女声' : gender;
    options.push({ label, value: `gender:${gender}` });
  });

  const languages = Array.from(new Set(list.map((v) => v.language).filter(Boolean) as string[]));
  languages.forEach((lang) => {
    const label = lang === 'zh' ? '中文' : lang === 'en' ? '英文' : lang;
    options.push({ label: `${label}音色`, value: `lang:${lang}` });
  });

  return options;
}

function filterVoices(list: VoiceType[], value: string): VoiceType[] {
  if (!value) return list;
  const [type, key] = value.split(':');
  if (type === 'gender') return list.filter((v) => v.gender === key);
  if (type === 'lang') return list.filter((v) => v.language === key);
  return list;
}

function getGenderLabel(gender?: string): string {
  if (gender === 'male') return '男声';
  if (gender === 'female') return '女声';
  if (gender === 'neutral') return '中性';
  return '多样音色';
}

function getLanguageName(code?: string): string {
  const names: Record<string, string> = { zh: '中文', en: '英文', ja: '日文', ko: '韩文' };
  return names[code || ''] || code || '';
}

function getVoiceDisplayName(voice: VoiceType): string {
  return voice.display_name || voice.voice_name || '未命名音色';
}

function buildVoiceMeta(voice: VoiceType): string {
  const parts: string[] = [];
  if (voice.gender) parts.push(getGenderLabel(voice.gender));
  if (voice.language) parts.push(getLanguageName(voice.language));
  return parts.join(' · ');
}

interface VoiceSelectorProps {
  voices: VoiceType[];
  selectedVoice: VoiceType | null;
  onChange: (voice: VoiceType | null) => void;
}

export default function VoiceSelector({ voices, selectedVoice, onChange }: VoiceSelectorProps) {
  const [show, setShow] = useState(false);
  const [filterValue, setFilterValue] = useState('');
  const [pendingVoice, setPendingVoice] = useState<VoiceType | null>(null);

  const filters = buildVoiceFilters(voices);
  const filteredVoices = filterVoices(voices, filterValue);

  const handleOpen = useCallback(() => {
    setPendingVoice(selectedVoice);
    setShow(true);
    document.body.classList.add('voice-selector-modal-open');
  }, [selectedVoice]);

  const handleClose = useCallback(() => {
    setShow(false);
    setPendingVoice(null);
    document.body.classList.remove('voice-selector-modal-open');
  }, []);

  const handleConfirm = useCallback(() => {
    if (pendingVoice) {
      onChange(pendingVoice);
    }
    handleClose();
  }, [pendingVoice, onChange, handleClose]);

  const handleSelectPending = useCallback((voice: VoiceType) => {
    setPendingVoice(voice);
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    return () => {
      document.body.classList.remove('voice-selector-modal-open');
    };
  }, []);

  return (
    <>
      {selectedVoice ? (
        <div className="selected-voice-display" style={{ cursor: 'pointer' }} onClick={handleOpen}>
          <div className="selected-info">
            <div className="selected-name">{getVoiceDisplayName(selectedVoice)}</div>
            <div className="selected-meta">{buildVoiceMeta(selectedVoice)}</div>
          </div>
          <button className="btn btn-link text-fg px-0" type="button" onClick={handleOpen}>
            更换
          </button>
        </div>
      ) : (
        <div className="voice-placeholder" onClick={handleOpen}>
          <div className="placeholder-left">
            <div className="placeholder-icon">🎵</div>
            <div className="placeholder-text">
              <strong>请选择音色</strong>
              <span>可使用不同年龄、性别、声学特征的说话人</span>
            </div>
          </div>
          <div className="placeholder-arrow">›</div>
        </div>
      )}

      <button className="select-voice-btn w-100" type="button" onClick={handleOpen}>
        🎵 请选择音色
      </button>

      {show && (
        <div className="voice-modal show" onClick={handleBackdropClick}>
          <div className="voice-modal-dialog">
            <div className="voice-modal-header">
              <h5 className="mb-0">选择音色</h5>
              <button className="voice-modal-close" type="button" onClick={handleClose}>
                ✕
              </button>
            </div>
            <div className="voice-modal-filter">
              <label className="form-label">筛选条件</label>
              <select
                className="form-select"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
              >
                {filters.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="voice-modal-body">
              <div className="voice-selector">
                {filteredVoices.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-exclamation-circle text-muted fa-2x"></i>
                    <p className="mt-3 text-muted">暂无音色数据</p>
                  </div>
                ) : (
                  filteredVoices.map((voice) => {
                    const isActive = pendingVoice?.voice_type === voice.voice_type;
                    return (
                      <div
                        key={voice.voice_type}
                        className={`voice-card ${isActive ? 'selected' : ''}`}
                        onClick={() => handleSelectPending(voice)}
                      >
                        <div className="voice-card-header">
                          <div className="voice-name">{getVoiceDisplayName(voice)}</div>
                          <span className="voice-badge">{getGenderLabel(voice.gender)}</span>
                        </div>
                        <p className="voice-desc">{voice.description || '高品质音色，适合多场景播报'}</p>
                        {voice.language && (
                          <div className="voice-card-meta">
                            <span className="voice-meta-item">
                              <i className="fas fa-language me-1 text-fg-muted"></i>
                              {getLanguageName(voice.language)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <div className="voice-modal-footer">
              <button className="btn btn-outline-secondary" type="button" onClick={handleClose}>
                取消
              </button>
              <button
                className="btn btn-primary"
                type="button"
                disabled={!pendingVoice}
                onClick={handleConfirm}
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}